import { createUrl } from "@/redux/api";
import { BuilderType, PaginatedResponse } from "@/redux/types";
import {
  CommentQueryParams,
  CommentTags,
  FullCommentResponse,
  TopicQueryParams,
  TopicResponse,
} from "@/redux/comments/types";

const communityService = (builder: BuilderType<CommentTags>) => ({
  getTopics: builder.query<
    PaginatedResponse<TopicResponse> | TopicResponse[],
    TopicQueryParams
  >({
    query: (queryParams) => ({
      url: createUrl("/discussions/topics/", queryParams),
      method: "GET",
    }),
    providesTags: (result) =>
      result
        ? [
            ...(result?.results || result).map((i) => ({
              type: "Topics",
              id: i.id,
            })),
            { type: "Topics" },
          ]
        : [],
  }),
  createTopic: builder.mutation({
    query: (data) => ({
      url: "/discussions/topics/",
      method: "POST",
      data,
    }),
    invalidatesTags: (result) => (result ? [{ type: "Topics" }] : []),
  }),
  updateTopic: builder.mutation({
    query: ({ id, data }) => ({
      url: `/discussions/topics/${id}/`,
      method: "PUT",
      data,
    }),
    invalidatesTags: (result) =>
      result ? [{ type: "Topics", id: result.id }] : [],
  }),
  deleteTopic: builder.mutation({
    query: (id) => ({
      url: `/discussions/topics/${id}/`,
      method: "DELETE",
    }),
    invalidatesTags: (result, error, args) =>
      error ? [] : [{ type: "Topics", id: args }],
  }),
  getComments: builder.query<
    PaginatedResponse<FullCommentResponse> | FullCommentResponse[],
    CommentQueryParams
  >({
    query: (queryParams) => ({
      url: createUrl("/comments/", queryParams, [undefined]),
      method: "GET",
    }),
    providesTags: (result) =>
      result ? result.results.map((i) => ({ type: "Comment", id: i.id })) : [],
  }),
  createComment: builder.mutation({
    query: (data) => ({
      url: "/comments/",
      method: "POST",
      data,
    }),
    invalidatesTags: (result) =>
      result
        ? [
            ...result.attachedBlocks.map((block) => ({
              type: "DataBlockVersion",
              id: block.id,
            })),
            { type: "UserAnalytics" },
          ]
        : [],
  }),
  getComment: builder.query({
    query: (id) => ({
      url: `/comments/${id}/`,
      method: "GET",
    }),
    providesTags: (result) =>
      result ? [{ type: "Comment", id: result.id }] : [],
  }),
  updateComment: builder.mutation({
    query: ({ id, data }) => ({
      url: `/comments/${id}/`,
      method: "PUT",
      data,
    }),
    invalidatesTags: (result) =>
      result
        ? [
            { type: "Comment", id: result.id },
            { type: "UserAnalytics" },
            ...result.attachedBlocks.map((block) => ({
              type: "DataBlockVersion",
              id: block.id,
            })),
          ]
        : [],
  }),
  patchComment: builder.mutation({
    query: ({ id, data }) => ({
      url: `/comments/${id}/`,
      method: "PATCH",
      data,
    }),
    invalidatesTags: (result) =>
      result
        ? [
            ...result.attachedBlocks.map((block) => ({
              type: "DataBlockVersion",
              id: block.id,
            })),
            { type: "UserAnalytics" },
          ]
        : [],
  }),
  deleteComment: builder.mutation({
    query: (id) => ({
      url: `/comments/${id}/`,
      method: "DELETE",
    }),
    invalidatesTags: (_, errors, args) =>
      errors ? [] : [{ type: "Comment", id: args }, { type: "UserAnalytics" }],
  }),
  likeComment: builder.mutation({
    query: ({ id, isLiked }) => ({
      url: `/comments/${id}/likes/`,
      method: "POST",
      data: { isLiked },
    }),
  }),
  reportComment: builder.query({
    query: (id) => ({
      url: `/comments/${id}/report/`,
      method: "GET",
    }),
  }),
});

export default communityService;
